// Presentational helpers shared by all concepts (no data access).
import { AlarmClock, AlertCircle, AlertTriangle, CloudUpload, Flag, GitMerge, Repeat, Star } from "lucide-react";
import type { ReactNode } from "react";
import type { TaskWithFlags } from "@desk/contracts";
import { daysBetween, deadlineState, formatDay, formatInstant } from "@/lib/dates";
import type { SyncMark } from "@/data/outbox-core";
import { cn } from "@/lib/utils";
import { repeatLabel } from "@/lib/repeat";

/** Things-style deadline: "⚑ 3 days left", "Due today", "2 days overdue" (icon + text, never color alone). */
export function deadlineText(deadline: string, today: string): string {
  const d = daysBetween(today, deadline);
  if (d < 0) return d === -1 ? "1 day overdue" : `${-d} days overdue`;
  if (d === 0) return "Due today";
  if (d === 1) return "Tomorrow";
  if (d <= 14) return `${d} days left`;
  return formatDay(deadline, today);
}

export function DeadlineLabel({ deadline, today, className }: { deadline: string; today: string; className?: string }) {
  const state = deadlineState(deadline, today);
  const text = deadlineText(deadline, today);
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap",
        state === "overdue" && "font-medium text-overdue",
        state === "today" && "font-medium text-due",
        state === "soon" && "text-foreground/75",
        className,
      )}
      title={`Deadline ${formatDay(deadline, today, { long: true })}`}
    >
      {state === "overdue" ? <AlertCircle className="size-3.5" aria-hidden /> : <Flag className="size-3.5" aria-hidden />}
      <span>{text}</span>
    </span>
  );
}

export function SyncBadge({ mark, className }: { mark: SyncMark | null; className?: string }) {
  if (!mark) return null;
  if (mark === "pending") {
    return (
      <span className={cn("inline-flex items-center gap-1 text-subtle", className)} title="Saved on this device; waiting to sync">
        <CloudUpload className="size-3.5" aria-hidden />
        <span>Pending sync</span>
      </span>
    );
  }
  return (
    <span className={cn("inline-flex items-center gap-1 font-medium text-destructive", className)}>
      {mark === "conflict" ? <GitMerge className="size-3.5" aria-hidden /> : <AlertTriangle className="size-3.5" aria-hidden />}
      <span>{mark === "conflict" ? "Conflict" : "Sync failed"}</span>
    </span>
  );
}

export function SnoozeBadge({ task, timezone, className }: { task: TaskWithFlags; timezone: string; className?: string }) {
  if (!task.snoozedUntil || !task.flags.isSnoozed) return null;
  return (
    <span className={cn("inline-flex items-center gap-1 text-muted-foreground", className)} title="Snoozed">
      <AlarmClock className="size-3.5" aria-hidden />
      <span>{formatInstant(task.snoozedUntil, timezone)}</span>
    </span>
  );
}

/** Repeat glyph for open repeating tasks; the full rule is in the tooltip and accessible name. */
export function RepeatBadge({ task, className }: { task: TaskWithFlags; className?: string }) {
  if (!task.repeat || task.status === "done" || task.status === "cancelled") return null;
  const label = repeatLabel(task.repeat);
  return (
    <span className={cn("inline-flex shrink-0 text-subtle", className)} role="img" aria-label={label} title={label}>
      <Repeat className="size-3.5" aria-hidden />
    </span>
  );
}

/** The Today star uses a warm yellow as a semantic glyph (Things), always paired with the "Today" label or an aria-label. */
export function TodayStar({ className }: { className?: string }) {
  return <Star className={cn("size-3.5 fill-[var(--star)] text-[var(--star)]", className)} aria-label="On Today" />;
}

export function CarriedLabel({ from, today }: { from: string; today: string }) {
  return <span className="text-subtle">from {formatDay(from, today)}</span>;
}

/**
 * Overlay for a row's quick actions (Today, Snooze). It appears on hover, keyboard focus, selection,
 * or while one of its menus is open, and sits over the right edge of the row on an opaque backdrop
 * tinted like the row, so nothing in the row shifts. The left edge fades out over the metadata it
 * covers. Rows must be `relative` and use the `group/row` class.
 */
export function RowHoverActions({ children, selected, className }: { children: ReactNode; selected?: boolean; className?: string }) {
  if (!children) return null;
  return (
    <div
      data-row-actions
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className={cn(
        "absolute z-[1] items-center gap-0.5 pl-6",
        "[background:linear-gradient(var(--row-tint),var(--row-tint)),var(--row-base,var(--background))]",
        "[mask-image:linear-gradient(to_right,transparent,#000_22px)]",
        selected
          ? "flex [--row-tint:var(--selected)]"
          : "hidden [--row-tint:var(--hover)] group-hover/row:flex group-focus-visible/row:flex group-has-[:focus-visible]/row:flex group-has-[[data-state=open]]/row:flex",
        className,
      )}
    >
      {children}
    </div>
  );
}
